import './index.css';

import React, { useEffect, useState } from 'react';

import http from '../../../api/http';
import RecordSection from '../../view/RecordSection';

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const RecordHistorySection = ({ section, id }) => {
  const [events, setEvents] = useState([]);
  const [historyTruncated, setHistoryTruncated] = useState(false);
  const [asOfInput, setAsOfInput] = useState('');
  const [asOf, setAsOf] = useState('');
  const [reconstruction, setReconstruction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      setLoading(true);
      setError('');
      setEvents([]);
      setReconstruction(null);
      try {
        const params = asOf ? { asOf: new Date(asOf).toISOString() } : undefined;
        const { data } = await http.get(`/foundations/records/${encodeURIComponent(section)}/${encodeURIComponent(id)}/history`, { params });
        if (!Array.isArray(data.events)) throw new Error('The history response did not contain an event list.');
        if (active) {
          setEvents(data.events);
          setHistoryTruncated(Boolean(data.historyTruncated));
          setReconstruction(data.reconstruction || null);
        }
      } catch (requestError) {
        if (active) {
          setEvents([]);
          setHistoryTruncated(false);
          setReconstruction(null);
          setError(requestError?.response?.data?.message || requestError.message || 'Unable to load record history.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (section && id) loadHistory();
    return () => { active = false; };
  }, [section, id, asOf]);

  return (
    <RecordSection title="History" className="record-history">
      <form className="record-history-as-of" onSubmit={(event) => { event.preventDefault(); setAsOf(asOfInput); }}>
        <label>
          <span className="kv-label">View state as of</span>
          <input aria-label="View state as of" type="datetime-local" value={asOfInput} onChange={(event) => setAsOfInput(event.target.value)} />
        </label>
        <button className="button-primary" type="submit" disabled={loading}>View state</button>
        {asOf ? <button className="button-secondary" type="button" onClick={() => { setAsOfInput(''); setAsOf(''); }}>Current timeline</button> : null}
      </form>
      {loading ? <p>Loading history...</p> : null}
      {error ? <p className="error-text">{error}</p> : null}
      {!loading && !error && historyTruncated ? <p className="muted-text">Showing the most recent 500 events.</p> : null}
      {reconstruction ? (
        <div className="record-history-snapshot">
          <h4>Reconstructed state at {new Date(reconstruction.asOf).toLocaleString()}</h4>
          {reconstruction.exists ? (
            <pre>{JSON.stringify(reconstruction.state, null, 2)}</pre>
          ) : <p>The record did not exist at that time.</p>}
        </div>
      ) : null}
      {!loading && !error && !events.length ? <p className="muted-text">No recorded changes yet.</p> : null}
      {!loading && !error && events.length ? (
        <ol className="record-history-list">
          {events.map((event) => (
            <li key={event.id}>
              <div className="row-between">
                <strong>{event.action === 'delete' ? 'Deleted' : event.action === 'create' ? 'Created' : 'Updated'}</strong>
                <time dateTime={event.occurredAt}>{new Date(event.occurredAt).toLocaleString()}</time>
              </div>
              {Object.keys(event.changes || {}).length ? (
                <ul className="record-history-changes">
                  {Object.entries(event.changes).map(([field, change]) => (
                    <li key={field}>
                      <strong>{field}</strong>: {formatValue(change.from)} → {formatValue(change.to)}
                    </li>
                  ))}
                </ul>
              ) : null}
              {event.actorUserId ? <span className="muted-text">Actor: {event.actorUserId}</span> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </RecordSection>
  );
};

export default RecordHistorySection;
